import { api } from '../lib/api';
import React, { useState } from 'react';
import { getField, getGrade, fmtM, isDeclined, missingFields } from '../lib/utils';
import Popup from './Popup';

const SCORE_CATS = [
  { k:'fire_protection',  label:'Fire Protection',  max:30, color:'#2dd4a0' },
  { k:'construction',     label:'Construction',     max:20, color:'#4c8dff' },
  { k:'occupancy_hazard', label:'Occupancy Hazard', max:20, color:'#a78bfa' },
  { k:'risk_management',  label:'Risk Management',  max:20, color:'#f0a830' },
  { k:'loss_history',     label:'Loss History',     max:10, color:'#2dd4a0' },
];

const FIELD_DEFS = [
  { n:'insured_name',       l:'Property Name'              },
  { n:'address',            l:'Address'                    },
  { n:'city',               l:'City'                       },
  { n:'state',              l:'State'                      },
  { n:'zip',                l:'Zip'                        },
  { n:'construction_type',  l:'Construction'               },
  { n:'year_built',         l:'Year Built'                 },
  { n:'square_footage',     l:'Sq Footage'                 },
  { n:'occupancy_class',    l:'Occupancy'                  },
  { n:'tiv',                l:'TIV',       click:'tiv'     },
  { n:'sprinkler_system',   l:'Sprinkler'                  },
  { n:'alarm_system',       l:'Alarm'                      },
  { n:'prior_losses_count', l:'Loss History', click:'losses' },
];

export default function DetailPanel({ submission: sub, onClose, onRefresh }) {
  const [popup, setPopup] = useState(null);

  const ev    = sub.evaluation;
  const st    = sub.status;
  const declined = isDeclined(sub);
  const name  = getField(sub, 'insured_name') || 'Unknown';
  const state = getField(sub, 'state') || '—';
  const occ   = getField(sub, 'occupancy_class') || '—';
  const tiv   = getField(sub, 'tiv');
  const grade = getGrade(ev?.score_total);

  const fm = {};
  (sub.fields || []).forEach(f => { fm[f.field_name] = f; });

  function fieldStatus(name) {
    const f = fm[name];
    if (!f || !f.field_value) return 'missing';
    if (f.confidence === 'high') return 'confirmed';
    return 'pending';
  }

async function handleAction(action) {
  const id = sub.id;
  try {
    if (action === 'send_nbi') {
      await api.updateStatus(id, 'nbi_sent');
      onRefresh();
    }
    if (action === 'trigger_assessment') {
      await api.updateStatus(id, 'assessment_sent');
      onRefresh();
    }
    if (action === 'send_quote') {
      await api.updateStatus(id, 'quote_sent');
      onRefresh();
    }
    if (action === 'ready_to_bind') {
      await api.updateStatus(id, 'ready_to_bind');
      onRefresh();
    }
    if (action === 'bind') {
      await api.updateStatus(id, 'policy_issuance');
      onRefresh();
    }
    if (action === 'request_info') {
      const note = window.prompt('What additional information is needed from the insured?');
      if (note) {
        await api.updateStatus(id, 'in_progress', note);
        onRefresh();
      }
    }
    if (action === 'refer') {
      const reason = window.prompt('Reason for referral:');
      if (reason) {
        await api.updateStatus(id, 'referred', reason);
        onRefresh();
      }
    }
    if (action === 'decline') {
      const reason = window.prompt('Reason for decline:');
      if (reason) {
        await api.updateStatus(id, 'declined', reason);
        onRefresh();
        onClose();
      }
    }
    if (action === 'not_taken') {
      const reason = window.prompt('Reason not taken up:');
      if (reason) {
        await api.updateStatus(id, 'not_taken', reason);
        onRefresh();
        onClose();
      }
    }
    if (action === 'cancel') {
      const reason = window.prompt('Reason for cancellation:');
      if (reason) {
        await api.updateStatus(id, 'cancelled', reason);
        onRefresh();
        onClose();
      }
    }
  } catch (err) {
    alert(`Error: ${err.message}`);
  }
}

  // Button logic by status — mirrors VALID_TRANSITIONS in backend/index.js
  const buttons = (() => {
    const RED    = { background:'var(--red-bg)',    borderColor:'var(--red-b)',    color:'var(--red)'    };
    const AMBER  = { background:'var(--amber-bg)',  borderColor:'var(--amber-b)',  color:'var(--amber)'  };
    const GREEN  = { background:'var(--green-bg)',  borderColor:'var(--green-b)',  color:'var(--green)', fontWeight:600, flex:2 };
    const BLUE   = { background:'var(--blue-bg)',   borderColor:'var(--blue-b)',   color:'var(--accent)', fontWeight:600, flex:2 };
    const MUTED  = { background:'var(--surface2)',  borderColor:'var(--border2)',  color:'var(--muted2)' };
    const waiting = (msg) => (
      <div style={{ fontSize:12, color:'var(--muted)', fontStyle:'italic', padding:'8px 0' }}>{msg}</div>
    );

    switch (st) {
      case 'processing':
      case 'enriching':
        return waiting('Parsing and enrichment in progress.');

      case 'nbi_ready':
        return (
          <>
            <ActionBtn onClick={() => handleAction('send_nbi')} style={BLUE}>
              Send NBI
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('refer')} style={AMBER}>Refer</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'data_incomplete':
        return (
          <>
            <ActionBtn onClick={() => handleAction('trigger_assessment')} style={BLUE}>
              Trigger Assessment
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('refer')} style={AMBER}>Refer</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'nbi_sent':
        return (
          <>
            <ActionBtn onClick={() => handleAction('trigger_assessment')} style={BLUE}>
              Trigger Assessment
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('not_taken')} style={MUTED}>Not Taken</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'assessment_sent':
        return (
          <>
            {waiting('Assessment link issued — awaiting insured.')}
            <ActionBtn onClick={() => handleAction('cancel')} style={RED}>Cancel</ActionBtn>
          </>
        );

      case 'in_progress':
        return waiting('Insured is completing the assessment.');

      case 'ready_to_quote':
        return (
          <>
            <ActionBtn onClick={() => handleAction('send_quote')} style={GREEN}>
              ✓ Send Quote
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('request_info')} style={AMBER}>
              Request More Info
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('refer')} style={AMBER}>Refer</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'review_incomplete': {
        const gaps = missingFields(sub);
        return (
          <>
            <div style={{ fontSize:12, color:'var(--amber)', padding:'8px 0', width:'100%' }}>
              Assessment complete but {gaps.length} field{gaps.length === 1 ? '' : 's'} still missing:
              {' '}{gaps.join(', ')}
            </div>
            <ActionBtn onClick={() => handleAction('request_info')} style={AMBER}>
              Request More Info
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('refer')} style={AMBER}>Refer</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );
      }

      case 'quote_sent':
        return (
          <>
            <ActionBtn onClick={() => handleAction('ready_to_bind')} style={GREEN}>
              ✓ Ready to Bind
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('not_taken')} style={MUTED}>Not Taken</ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'ready_to_bind':
        return (
          <>
            <ActionBtn onClick={() => handleAction('bind')} style={GREEN}>
              ✓ Issue Policy
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('not_taken')} style={MUTED}>Not Taken</ActionBtn>
          </>
        );

      case 'referred':
        return (
          <>
            <ActionBtn onClick={() => handleAction('send_nbi')} style={BLUE}>
              Approve — Send NBI
            </ActionBtn>
            <ActionBtn onClick={() => handleAction('decline')} style={RED}>Decline</ActionBtn>
          </>
        );

      case 'policy_issuance':
        return waiting('Policy bound.');

      case 'appetite_decline':
        return waiting('Declined on appetite rules.');

      case 'declined':
        return waiting('Declined.');

      case 'not_taken':
        return waiting('Not taken up.');

      case 'cancelled':
        return waiting('Cancelled.');

      default:
        return null;
    }
  })();

  return (
    <>
      <div
        onClick={onClose}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:99 }}
      />
      <div style={{
        position:'fixed', right:0, top:56, bottom:0, width:560,
        background:'var(--surface)', borderLeft:'1px solid var(--border)',
        overflowY:'auto', zIndex:100,
      }}>

        {/* Header */}
        <div style={{
          padding:'22px 28px', borderBottom:'1px solid var(--border)',
          position:'sticky', top:0, background:'var(--surface)', zIndex:10,
        }}>
          <div style={{
            fontSize:10, color:'var(--muted)', letterSpacing:'0.08em',
            textTransform:'uppercase', marginBottom:5,
          }}>
            {sub.reference_number}
          </div>
          <div style={{
            fontFamily:'var(--serif)', fontSize:20,
            fontWeight:400, letterSpacing:'-0.02em',
          }}>
            {name}
          </div>
          <div style={{ fontSize:11, color:'var(--muted2)', marginTop:4 }}>
            {[occ, state, tiv ? 'TIV ' + fmtM(parseFloat(tiv)) : ''].filter(Boolean).join(' · ')}
          </div>
          <div
            onClick={onClose}
            style={{
              position:'absolute', right:20, top:20, width:28, height:28,
              borderRadius:4, background:'var(--surface2)', border:'1px solid var(--border)',
              cursor:'pointer', display:'flex', alignItems:'center',
              justifyContent:'center', color:'var(--muted)', fontSize:14,
            }}
          >✕</div>
        </div>

        {/* Decline banner */}
        {declined && (
          <div style={{ padding:'12px 28px 0' }}>
            <div style={{
              background:'var(--red-bg)', border:'1px solid var(--red-b)',
              borderRadius:4, padding:12, fontSize:12,
              color:'var(--red)', lineHeight:1.6,
            }}>
              {st === 'appetite_decline'
                ? 'Declined on appetite rules.'
                : 'Declined by underwriter.'}
              {sub.decline_reason ? ` ${sub.decline_reason}` : ''}
            </div>
          </div>
        )}

        {/* Risk score */}
        {ev?.score_breakdown && (
          <Section title="Risk Score">
            <div style={{ fontSize:24, fontWeight:500, color:grade.color, marginBottom:12 }}>
              {grade.label}
            </div>
            {SCORE_CATS.map(cat => {
              const pts = ev.score_breakdown[cat.k] || 0;
              const pct = Math.round(pts / cat.max * 100);
              return (
                <div key={cat.k} style={{ marginBottom:9 }}>
                  <div style={{
                    display:'flex', justifyContent:'space-between',
                    fontSize:11, marginBottom:4, color:'var(--muted2)',
                  }}>
                    <span>{cat.label}</span>
                  </div>
                  <div style={{ height:4, background:'var(--surface3)', borderRadius:2, overflow:'hidden' }}>
                    <div style={{
                      height:'100%', width:`${pct}%`,
                      background:cat.color, borderRadius:2, transition:'width 0.5s',
                    }}/>
                  </div>
                </div>
              );
            })}
          </Section>
        )}

        {/* Loss expectancies */}
        {(ev?.nle_value || ev?.pricing_breakdown?.pml_value) && (
          <Section title="Loss Expectancies">
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr>
                  {['Scenario','Amount'].map(h => (
                    <th key={h} style={{
                      textAlign:'left', padding:'6px 10px', fontSize:10,
                      letterSpacing:'0.06em', textTransform:'uppercase',
                      color:'var(--muted)', background:'var(--surface2)',
                      borderBottom:'1px solid var(--border)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['NLE', ev.nle_value],
                  ['PML', ev.pricing_breakdown?.pml_value],
                  ['MFL', ev.pricing_breakdown?.mfl_value],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 10px', color:'var(--muted2)' }}>{label}</td>
                    <td style={{ padding:'8px 10px', fontWeight:500 }}>{fmtM(val)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* Property data */}
        <Section title={
          <span>
            Property Data
            <span style={{ fontSize:9, color:'var(--muted)', fontWeight:400, marginLeft:8 }}>
              <span style={{ color:'var(--green)' }}>●</span> confirmed
              &nbsp;<span style={{ color:'var(--amber)' }}>●</span> pending assessment
            </span>
          </span>
        }>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
            {FIELD_DEFS.map(def => {
              const fst  = fieldStatus(def.n);
              const val  = fm[def.n]?.field_value;
              const borderColor = fst === 'confirmed' ? 'var(--green)' : 'var(--amber)';
              return (
                <div
                  key={def.n}
                  onClick={() => def.click && val && setPopup(def.click)}
                  style={{
                    background:'var(--surface2)', border:'1px solid var(--border)',
                    borderRadius:4, padding:'9px 11px', position:'relative',
                    overflow:'hidden',
                    cursor: def.click && val ? 'pointer' : 'default',
                    borderLeft:`2px solid ${borderColor}`,
                  }}
                >
                  <div style={{
                    fontSize:9, letterSpacing:'0.08em', textTransform:'uppercase',
                    color:'var(--muted)', marginBottom:3,
                  }}>
                    {def.l}
                  </div>
                  <div style={{
                    fontSize:12,
                    fontWeight: val ? 500 : 400,
                    color: val ? 'var(--text)' : 'var(--muted)',
                    fontStyle: val ? 'normal' : 'italic',
                  }}>
                    {val || 'Pending Assessment'}
                  </div>
                  {def.click && val && (
                    <div style={{ fontSize:9, color:'var(--accent)', marginTop:2 }}>
                      ↗ click for detail
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Preliminary risk summary */}
        {sub.narrative && (
          <Section title={
            <span>
              Preliminary Risk Summary
              <span style={{ fontSize:9, color:'var(--muted)', marginLeft:8 }}>AI-generated</span>
            </span>
          }>
            <div style={{
              background:'var(--surface2)', border:'1px solid var(--border)',
              borderRadius:4, padding:14, fontSize:12, color:'var(--muted2)',
              lineHeight:1.8, whiteSpace:'pre-wrap', maxHeight:280, overflowY:'auto',
            }}>
              {sub.narrative}
            </div>
          </Section>
        )}

        {/* Actions */}
        <div style={{
          padding:'16px 28px', display:'flex', gap:8,
          position:'sticky', bottom:0,
          background:'var(--surface)', borderTop:'1px solid var(--border)',
        }}>
          {buttons}
        </div>

      </div>

      {/* Popups */}
      {popup === 'tiv' && (
        <Popup title="TIV Breakdown" onClose={() => setPopup(null)}>
          <TIVBreakdown sub={sub} />
        </Popup>
      )}
      {popup === 'losses' && (
        <Popup
          title={`Loss History — ${getField(sub,'prior_losses_count') || '0'} losses`}
          onClose={() => setPopup(null)}
        >
          <LossHistory sub={sub} />
        </Popup>
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ padding:'16px 28px', borderBottom:'1px solid var(--border)' }}>
      <div style={{
        fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase',
        color:'var(--muted)', marginBottom:12,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ActionBtn({ children, disabled, onClick, style }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        flex:1, height:38, borderRadius:4, fontSize:11, fontWeight:500,
        letterSpacing:'0.05em', textTransform:'uppercase', border:'1px solid',
        display:'flex', alignItems:'center', justifyContent:'center', gap:5,
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition:'all 0.15s', ...style,
      }}
    >
      {children}
    </button>
  );
}

function TIVBreakdown({ sub }) {
  const tiv = parseFloat(getField(sub, 'tiv') || 0);
  const bd  = sub.tiv_breakdown;
  return (
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
      <thead>
        <tr>
          {['Component','Amount','Note'].map(h => (
            <th key={h} style={{
              textAlign:'left', padding:'6px 10px', fontSize:10,
              textTransform:'uppercase', letterSpacing:'0.06em',
              color:'var(--muted)', borderBottom:'1px solid var(--border)',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[
          ['Building',              bd?.building],
          ['Equipment',             bd?.equipment],
          ['Stock / Inventory',     bd?.stock],
          ['Business Interruption', bd?.bi],
        ].map(([label, val]) => (
          <tr key={label} style={{ borderBottom:'1px solid var(--border)' }}>
            <td style={{ padding:'8px 10px' }}>{label}</td>
            <td style={{ padding:'8px 10px' }}>{val ? fmtM(val) : '—'}</td>
            <td style={{ padding:'8px 10px', color:'var(--muted)', fontSize:11 }}>
              {val ? 'Submitted' : 'Pending'}
            </td>
          </tr>
        ))}
        <tr>
          <td style={{ padding:'8px 10px', fontWeight:500 }}>Total TIV</td>
          <td style={{ padding:'8px 10px', fontWeight:500 }}>{fmtM(tiv)}</td>
          <td style={{ padding:'8px 10px', color:'var(--muted)', fontSize:11 }}>Submitted</td>
        </tr>
      </tbody>
    </table>
  );
}

function LossHistory({ sub }) {
  const losses = sub.losses || [];
  const total  = losses.reduce((s, l) => s + (l.amount || 0), 0);
  const avg    = losses.length ? Math.round(total / losses.length) : 0;
  return losses.length ? (
    <>
      <div style={{ display:'flex', gap:24, marginBottom:16 }}>
        <div>
          <div style={{
            fontSize:10, color:'var(--muted)', textTransform:'uppercase',
            letterSpacing:'0.06em', marginBottom:4,
          }}>Total</div>
          <div style={{ fontSize:20, fontWeight:500 }}>{fmtM(total)}</div>
        </div>
        <div>
          <div style={{
            fontSize:10, color:'var(--muted)', textTransform:'uppercase',
            letterSpacing:'0.06em', marginBottom:4,
          }}>Average</div>
          <div style={{ fontSize:20, fontWeight:500 }}>{fmtM(avg)}</div>
        </div>
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr>
            {['Date','Cause of Loss','Amount','Status'].map(h => (
              <th key={h} style={{
                textAlign:'left', padding:'6px 10px', fontSize:10,
                textTransform:'uppercase', letterSpacing:'0.06em',
                color:'var(--muted)', borderBottom:'1px solid var(--border)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {losses.map((l, i) => (
            <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
              <td style={{ padding:'8px 10px' }}>{l.date || '—'}</td>
              <td style={{ padding:'8px 10px' }}>{l.cause || '—'}</td>
              <td style={{ padding:'8px 10px' }}>{fmtM(l.amount || 0)}</td>
              <td style={{ padding:'8px 10px', color:'var(--muted2)' }}>{l.status || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  ) : (
    <div style={{ color:'var(--muted)', padding:'24px 0', textAlign:'center' }}>
      No losses reported.
    </div>
  );
}